import { ManageSearchOutlined, TravelExploreOutlined } from '@mui/icons-material';
import { Box, Skeleton, styled } from '@mui/material';
import { Link } from 'react-router-dom';
import { BodyText } from '@/components/BodyText';
import { useGetRecentPartFinderActivity } from '@/hooks/api/part-finder-reports/useGetRecentPartFinderActivity';
import { useTenants } from '@/services/tenants/useTenants';
import { DashboardTile } from './DashboardTile';
import { RecentPartFinderActivityRow } from './RecentPartFinderActivityRow';
import { TileEmptyState } from './TileEmptyState';

const RowList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
});

const ViewAllLink = styled(Link)(({ theme }) => ({
  flexShrink: 0,
  fontSize: '0.875rem',
  textDecoration: 'none',
  color: theme.palette.secondary.main,
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export const PartFinderTile = () => {
  const { getPath } = useTenants();
  const { data: activity, isLoading, isError } = useGetRecentPartFinderActivity();
  const partFinderPath = getPath('part-finder');

  const entries = activity ?? [];

  return (
    <DashboardTile
      id='part-finder'
      title='Part Finder'
      icon={<TravelExploreOutlined />}
      to={partFinderPath}
      action={<ViewAllLink to={partFinderPath}>View all</ViewAllLink>}
    >
      {isError && <BodyText color='text.secondary'>Failed to load part finder activity.</BodyText>}
      {!isError && isLoading && <Skeleton variant='rounded' height={120} />}
      {!isError && !isLoading && entries.length === 0 && (
        <TileEmptyState
          testId='no-part-finder-activity'
          graphic={<ManageSearchOutlined />}
          title='No lookups yet'
          description='Look up a part number to find sourcing options. Recent lookups show here.'
          action={{ label: 'Look up a part number', to: partFinderPath }}
        />
      )}
      {!isError && !isLoading && entries.length > 0 && (
        <RowList>
          {entries.map(entry => (
            <RecentPartFinderActivityRow
              key={entry.type === 'batch' ? `batch-${entry.batch.id}` : `report-${entry.report.id}`}
              entry={entry}
            />
          ))}
        </RowList>
      )}
    </DashboardTile>
  );
};
